/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { ChallengeType } from "../../../service/api-response/Challenges";
import { TableRow } from "../../../components/table-components/TableRow";
import { routes } from "../../../appRoutingHelpers";
import { StyledText } from "../../../assets/typography/typography";
import { colors } from "../../../components/colors";
import { InLineLabel } from "../../../components/Label";
import { useSelector } from "react-redux";
import * as authSelectors from "../../auth/authSelectors";
import { ChallengesTableCell } from "./ChallengesTableCell";
import { formatDateWithYear } from "../../../util";

type ChallengeRowProps = {
  challenge: ChallengeType;
  isCurrent: boolean;
  isActive: boolean;
};

const imageStyle = css`
  object-fit: cover;
  min-width: 32px;
  min-height: 32px;
  width: 32px;
  height: 32px;
  border-radius: 9999px;
`;

const cellStyle = css`
  white-space: nowrap;
`;

export const ChallengeRow = ({
  challenge,
  isCurrent,
  isActive,
}: ChallengeRowProps) => {
  const organization = useSelector(authSelectors.selectedOrganizationKeyName);
  const href = routes.challengesEdit(organization, challenge.id!);
  const activeAndCurrent = isCurrent && isActive;

  const returnFontStyle = () => {
    return isCurrent ? "bodyTextSemibold" : "bodyTextRegular";
  };

  return (
    <TableRow>
      <ChallengesTableCell isCurrent={isCurrent} href={href}>
        <img css={imageStyle} src={challenge.imageUrl} alt="challenge" />
      </ChallengesTableCell>
      <ChallengesTableCell isCurrent={isCurrent} href={href}>
        <StyledText
          className={`${returnFontStyle()} mb-0`}
          color={colors.textPrimary}
        >
          {challenge.title}
        </StyledText>
      </ChallengesTableCell>
      <ChallengesTableCell isCurrent={isCurrent} href={href}>
        <StyledText
          css={cellStyle}
          className={`${returnFontStyle()} mb-0`}
          color={colors.textPrimary}
        >
          {isCurrent ? challenge.version + " (Current)" : challenge.version}
        </StyledText>
      </ChallengesTableCell>
      <ChallengesTableCell isCurrent={isCurrent} href={href}>
        <InLineLabel
          backgroundColor={
            challenge.isInUse ? colors.successLight : colors.errorLight
          }
          color={challenge.isInUse ? colors.successDark : colors.errorDark}
        >
          {challenge.isInUse ? "Yes" : "No"}
        </InLineLabel>
      </ChallengesTableCell>
      <ChallengesTableCell isCurrent={isCurrent} href={href}>
        <InLineLabel
          backgroundColor={
            activeAndCurrent && challenge.enabled
              ? colors.successLight
              : colors.errorLight
          }
          color={
            activeAndCurrent && challenge.enabled
              ? colors.successDark
              : colors.errorDark
          }
        >
          {activeAndCurrent && challenge.enabled ? "Yes, enabled" : "No"}
        </InLineLabel>
      </ChallengesTableCell>
      <ChallengesTableCell isCurrent={isCurrent} href={href}>
        <StyledText
          className={`${returnFontStyle()} mb-0`}
          color={colors.textPrimary}
        >
          {formatDateWithYear(challenge.activeFrom)}
        </StyledText>
      </ChallengesTableCell>
      <ChallengesTableCell isCurrent={isCurrent} href={href}>
        <StyledText
          className={`${returnFontStyle()} mb-0`}
          color={colors.textPrimary}
        >
          {formatDateWithYear(challenge.activeExpiresAt)}
        </StyledText>
      </ChallengesTableCell>
      <ChallengesTableCell isCurrent={isCurrent} href={href}>
        <StyledText
          className={`${returnFontStyle()} mb-0`}
          color={colors.textPrimary}
        >
          {formatDateWithYear(challenge.completedExpiresAt)}
        </StyledText>
      </ChallengesTableCell>
      <ChallengesTableCell isCurrent={isCurrent} href={href}>
        <InLineLabel
          backgroundColor={colors.ctaInactive}
          color={colors.textPrimary}
        >
          {challenge.autoClaimReward ? "True" : "False"}
        </InLineLabel>
      </ChallengesTableCell>
    </TableRow>
  );
};
